import { useCallback, useEffect, useState } from 'react';

export type SchemaPanelVariant = 'create' | 'edit' | 'overview';

const storageKey = (variant: SchemaPanelVariant) => `schema-panel:${variant}`;

/**
 * Persisted open/closed state for the embedded Schema Explorer panel.
 * Defaults to closed on first visit; preference survives page reloads
 * and is scoped per screen variant so each flow remembers independently.
 */
export function useSchemaPanelState(variant: SchemaPanelVariant) {
    const [open, setOpenState] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        try {
            return window.localStorage.getItem(storageKey(variant)) === '1';
        } catch {
            return false;
        }
    });

    const setOpen = useCallback((next: boolean) => {
        setOpenState(next);
        try {
            window.localStorage.setItem(storageKey(variant), next ? '1' : '0');
        } catch {
            /* ignore storage errors (e.g. private mode) */
        }
    }, [variant]);

    const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

    // Listen for storage changes from other tabs so panels stay in sync.
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === storageKey(variant) && e.newValue !== null) {
                setOpenState(e.newValue === '1');
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [variant]);

    return { open, setOpen, toggle };
}
